export type ExtensionErrorName =
  | 'HOME_SCREEN_NOT_FOUND'
  | 'NOT_LOGGED_IN'
  | 'SOAP_TEMPLATE_NOT_FOUND'
  | 'NOTE_EDITOR_NOT_FOUND'
  | 'FAILED_TO_SIGN'
  | 'GENERIC_ERROR'
  | 'TIMEOUT'
  | 'EXTENSION_VERSION_OUTDATED'
  | 'FAILED_TO_IMPORT'
  | 'TEMPLATE_NOT_SUPPORTED'
  | 'STAFF_MISMATCH'
  | 'INVALID_STATE_PAGE_REFRESH_REQUIRED'
  | 'MIC_DISCONNECTED'
  | 'MIC_PERMISSIONS_DENIED';

export type HumanFriendlyError = {
  title: string;
  message: string;
};

export const SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS: Record<
  ExtensionErrorName,
  HumanFriendlyError
> = {
  EXTENSION_VERSION_OUTDATED: {
    title: 'Eluve extension outdated',
    message:
      'The Eluve extension is out of date. To update the extension to the latest version, refresh the page, and try again.',
  },
  HOME_SCREEN_NOT_FOUND: {
    title: 'Home screen unavailable',
    message:
      "We're sorry, but we couldn't load EHR home screen. Please try refreshing the page.",
  },
  NOT_LOGGED_IN: {
    title: 'Not authorized',
    message: 'Please sign in to your EHR account in a new tab and try again.',
  },
  SOAP_TEMPLATE_NOT_FOUND: {
    title: 'Template missing',
    message:
      'Template not found: "SOAP Template". Please check if the template exists and try again.',
  },
  NOTE_EDITOR_NOT_FOUND: {
    title: 'Note editor unavailable',
    message:
      "We couldn't locate the note editor within EHR. Please ensure it's present and try again.",
  },
  TEMPLATE_NOT_SUPPORTED: {
    title: 'Template not supported',
    message:
      'The selected template is not supported by the EHR. Please select a different template and try again.',
  },
  FAILED_TO_SIGN: {
    title: 'Chart entry signature failed',
    message:
      'We encountered an issue while signing the chart entry. Please try again.',
  },
  GENERIC_ERROR: {
    title: 'Something went wrong',
    message:
      'Our team is investigating the issue. You can try again, or email support@eluve.com if you need assistance.',
  },
  TIMEOUT: {
    title: 'Request timed out',
    message:
      'Your request took too long to process. Please check your internet connection and try again.',
  },
  FAILED_TO_IMPORT: {
    title: 'Import failed',
    message: 'Failed to import data. Please check the data and try again.',
  },
  STAFF_MISMATCH: {
    title: 'Appointment owner mismatch',
    message:
      'The appointment owner does not match the current user in EHR. Please ensure you are logged in as the correct user and try again.',
  },
  INVALID_STATE_PAGE_REFRESH_REQUIRED: {
    title: 'Session expired',
    message:
      "We've noticed you're in an inconsistent state. Please refresh the page to continue.",
  },
  MIC_DISCONNECTED: {
    title: 'Microphone not detected',
    message:
      'Your recording has been paused. Please update your microphone selection in order to resume.',
  },
  MIC_PERMISSIONS_DENIED: {
    title: 'Microphone access required',
    message:
      'Eluve needs permission to access your microphone to record audio.',
  },
} as const;

export const ERRORS = Object.keys(SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS).reduce(
  (acc, key) => {
    acc[key as keyof typeof SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS] =
      key as keyof typeof SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS;
    return acc;
  },
  {} as Record<
    keyof typeof SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS,
    keyof typeof SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS
  >,
);

export const getHumanFriendlyError = (error?: ExtensionErrorName | string) => {
  if (error && error in ERRORS) {
    return SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS[error as ExtensionErrorName];
  }
  return SYNC_ERRORS_TO_HUMAN_FRIENDLY_ERRORS.GENERIC_ERROR;
};
